/**
 * PDF Cleanup Module
 *
 * Cleans extracted PDF text by removing:
 * - Page numbers
 * - Repeated headers/footers
 * - Noise lines with high digit/punctuation ratio
 *
 * Also detects likely scanned/image-only PDFs.
 */

import type { ExtractionResult, PageResult } from './pdfExtract';

/**
 * Cleanup result
 */
export interface CleanupResult {
  cleanedText: string;
  rawText: string;
  isLikelyScanned: boolean;
  removedCount: number;
}

/**
 * Thresholds for scanned PDF detection
 */
const SCANNED_THRESHOLDS = {
  /** Minimum total characters to consider as text PDF */
  MIN_TOTAL_CHARS: 500,
  /** Minimum average characters per page */
  MIN_AVG_CHARS_PER_PAGE: 80,
} as const;

/**
 * Cleanup configuration
 */
const CLEANUP_CONFIG = {
  /** Percentage of pages a line must appear on to be considered repeated */
  REPEATED_LINE_THRESHOLD: 0.6,
  /** Minimum pages for repeated line detection */
  MIN_PAGES_FOR_REPEATED: 3,
  /** Percentage of line that is digits/punctuation to be considered noise */
  NOISE_RATIO_THRESHOLD: 0.5,
  /** Header zone: top percentage of page (by line position) */
  HEADER_ZONE_PERCENT: 0.1,
  /** Footer zone: bottom percentage of page (by line position) */
  FOOTER_ZONE_PERCENT: 0.1,
} as const;

/**
 * Page number patterns
 */
const PAGE_NUMBER_PATTERNS = [
  /^\d{1,4}$/, // Just a number: "1", "42", "123"
  /^page\s+\d+$/i, // "Page 1", "page 42"
  /^\d+\s*\/\s*\d+$/, // "1/10", "42 / 100"
  /^-\s*\d+\s*-$/, // "- 1 -", "-42-"
  /^\[\s*\d+\s*\]$/, // "[1]", "[ 42 ]"
  /^—\s*\d+\s*—$/, // "— 1 —"
];

/**
 * Clean extracted PDF text
 *
 * @param extraction - Extraction result from pdfExtract
 * @returns Cleanup result with cleaned and raw text
 */
export function cleanPdfText(extraction: ExtractionResult): CleanupResult {
  const rawText = extraction.fullText;

  // Check if likely scanned
  const isLikelyScanned = detectScannedPdf(extraction);

  if (isLikelyScanned) {
    return {
      cleanedText: '',
      rawText,
      isLikelyScanned: true,
      removedCount: 0,
    };
  }

  // Find repeated lines (headers/footers)
  const repeatedLines = findRepeatedLines(extraction.pages);

  // Process each page
  const cleanedPages: string[] = [];
  let removedCount = 0;

  for (const page of extraction.pages) {
    const lines = page.text.split('\n');
    const totalLines = lines.length;
    const cleanedLines: string[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      // Skip empty lines (but don't count as removed)
      if (!line) continue;

      // Check if in header/footer zone
      const linePosition = totalLines > 1 ? i / (totalLines - 1) : 0.5;
      const inHeaderZone = linePosition < CLEANUP_CONFIG.HEADER_ZONE_PERCENT;
      const inFooterZone = linePosition > 1 - CLEANUP_CONFIG.FOOTER_ZONE_PERCENT;

      // Check various removal criteria
      if (isPageNumber(line)) {
        removedCount++;
        continue;
      }

      if (isNoiseLine(line)) {
        removedCount++;
        continue;
      }

      // Only check repeated lines in header/footer zones
      if ((inHeaderZone || inFooterZone) && repeatedLines.has(normalizeLine(line))) {
        removedCount++;
        continue;
      }

      cleanedLines.push(line);
    }

    if (cleanedLines.length > 0) {
      cleanedPages.push(cleanedLines.join(' '));
    }
  }

  const cleanedText = cleanedPages.join('\n\n');

  return {
    cleanedText,
    rawText,
    isLikelyScanned: false,
    removedCount,
  };
}

/**
 * Detect if PDF is likely scanned/image-only
 */
export function detectScannedPdf(extraction: ExtractionResult): boolean {
  return (
    extraction.totalChars < SCANNED_THRESHOLDS.MIN_TOTAL_CHARS ||
    extraction.avgCharsPerPage < SCANNED_THRESHOLDS.MIN_AVG_CHARS_PER_PAGE
  );
}

/**
 * Check if a line matches page number patterns
 */
export function isPageNumber(line: string): boolean {
  const trimmed = line.trim();
  return PAGE_NUMBER_PATTERNS.some((pattern) => pattern.test(trimmed));
}

/**
 * Check if a line is noise (high ratio of digits/punctuation)
 */
export function isNoiseLine(line: string): boolean {
  const trimmed = line.trim();
  if (trimmed.length < 3) return false; // Too short to judge

  // Count digits and punctuation (excluding spaces)
  const noiseChars = trimmed.replace(/[\s]/g, '').replace(/[a-zA-Z]/g, '').length;
  const totalChars = trimmed.replace(/[\s]/g, '').length;

  if (totalChars === 0) return true;

  const noiseRatio = noiseChars / totalChars;
  return noiseRatio >= CLEANUP_CONFIG.NOISE_RATIO_THRESHOLD;
}

/**
 * Find lines that repeat across multiple pages (headers/footers)
 */
function findRepeatedLines(pages: PageResult[]): Set<string> {
  if (pages.length < CLEANUP_CONFIG.MIN_PAGES_FOR_REPEATED) {
    return new Set();
  }

  // Count occurrences of each normalized line
  const lineCounts = new Map<string, number>();

  for (const page of pages) {
    const lines = page.text.split('\n');
    const seenOnPage = new Set<string>();

    for (const line of lines) {
      const normalized = normalizeLine(line);
      if (normalized && !seenOnPage.has(normalized)) {
        seenOnPage.add(normalized);
        lineCounts.set(normalized, (lineCounts.get(normalized) || 0) + 1);
      }
    }
  }

  // Find lines appearing on enough pages
  const threshold = Math.ceil(pages.length * CLEANUP_CONFIG.REPEATED_LINE_THRESHOLD);
  const repeatedLines = new Set<string>();

  for (const [line, count] of lineCounts) {
    if (count >= threshold) {
      repeatedLines.add(line);
    }
  }

  return repeatedLines;
}

/**
 * Normalize a line for comparison (lowercase, collapse whitespace)
 */
function normalizeLine(line: string): string {
  return line.toLowerCase().replace(/\s+/g, ' ').trim();
}
